import React, { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';

export type InlineEditKind = 'text' | 'days' | 'offset';

export type InlineEditError = 'errNameRequired' | 'errDuration' | 'errStart' | 'errNumber';

/** Returns the locale key of the validation error for `raw`, or null when valid. */
export function validateInlineEdit(kind: InlineEditKind, raw: string): InlineEditError | null {
  const trimmed = raw.trim();
  if (kind === 'text') return trimmed ? null : 'errNameRequired';
  if (!trimmed || Number.isNaN(Number(trimmed))) return 'errNumber';
  const n = Number(trimmed);
  if (!Number.isInteger(n)) return kind === 'days' ? 'errDuration' : 'errStart';
  if (kind === 'days' && n < 1) return 'errDuration';
  if (kind === 'offset' && n < 0) return 'errStart';
  return null;
}

interface InlineEditableValueProps {
  value: string | number;
  kind: InlineEditKind;
  /** What is shown while not editing (e.g. "6 days", "Day 12"). */
  display: React.ReactNode;
  ariaLabel: string;
  testId: string;
  disabled?: boolean;
  className?: string;
  onCommit: (next: string | number) => void;
}

/**
 * Sprint 36, story-192. Click-to-edit cell. Enter or blur commits, Esc cancels. An invalid value
 * is never committed: the field keeps the user's text and focus and shows a translated message.
 * Committing an unchanged value sends nothing. When `disabled` (not READY, or a role that cannot
 * edit) it renders plain text with no edit affordance.
 */
export const InlineEditableValue: React.FC<InlineEditableValueProps> = ({
  value,
  kind,
  display,
  ariaLabel,
  testId,
  disabled,
  className,
  onCommit,
}) => {
  const t = useTranslation('taskTree');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const [error, setError] = useState<InlineEditError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);
  // Set once an edit session has committed, so Enter followed by the unmount blur sends one patch.
  const committedRef = useRef(false);
  const errorId = `${testId}-error`;

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  if (disabled) {
    return <span className={className}>{display}</span>;
  }

  const commit = () => {
    if (cancelledRef.current || committedRef.current) return;
    // Unchanged text is never validated or sent (e.g. a stored duration of 0 left as is).
    if (draft.trim() === String(value).trim()) {
      committedRef.current = true;
      setError(null);
      setEditing(false);
      return;
    }
    const problem = validateInlineEdit(kind, draft);
    if (problem) {
      setError(problem);
      inputRef.current?.focus();
      return;
    }
    committedRef.current = true;
    setError(null);
    setEditing(false);
    const next = kind === 'text' ? draft.trim() : Number(draft);
    if (next !== value) onCommit(next);
  };

  const cancel = () => {
    cancelledRef.current = true;
    committedRef.current = false;
    setDraft(String(value));
    setError(null);
    setEditing(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        data-testid={`${testId}-trigger`}
        onClick={() => {
          cancelledRef.current = false;
          committedRef.current = false;
          setDraft(String(value));
          setEditing(true);
        }}
        className={cn('group inline-flex items-center gap-1 rounded px-1 -mx-1 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100', className)}>
        {display}
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 group-focus:opacity-60 [@media(hover:none)]:opacity-40" aria-hidden />
      </button>
    );
  }

  return (
    <span className="inline-flex flex-col min-w-0">
      <input
        ref={inputRef}
        value={draft}
        inputMode={kind === 'text' ? undefined : 'numeric'}
        aria-label={ariaLabel}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        data-testid={testId}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError(null);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        className={cn(
          'text-sm bg-transparent border-b outline-none text-gray-900 dark:text-white min-w-0',
          kind === 'text' ? 'w-full flex-1' : 'w-16',
          error ? 'border-red-500' : 'border-gray-300',
        )}
      />
      {error && (
        <span id={errorId} role="alert" className="text-[11px] text-red-600 dark:text-red-400">
          {t[error]}
        </span>
      )}
    </span>
  );
};

export default InlineEditableValue;
